import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router'
import { Drawer } from '@/components/ui/Drawer'
import { useAuth } from '@/features/auth/useAuth'
import { CallProvider } from '@/features/messaging/components/CallProvider'
import { filterNavigation, type NavGroup } from '../navigation'
import { CommandPalette } from './CommandPalette'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'

interface AppShellProps {
  navigation: NavGroup[]
  subtitle: string
  homeHref: string
  accountHref: string
  searchable?: boolean
}

/**
 * Shared chrome for every portal: fixed sidebar on desktop, off-canvas drawer on tablet/phone,
 * sticky translucent top bar, skip link and the ⌘K command palette.
 */
export function AppShell({ navigation, subtitle, homeHref, accountHref, searchable = false }: AppShellProps) {
  const { t } = useTranslation()
  const { can, hasModule, school } = useAuth()
  const [navOpen, setNavOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const groups = useMemo(() => filterNavigation(navigation, can, hasModule), [navigation, can, hasModule])

  useEffect(() => {
    if (!searchable) return
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [searchable])

  const brand = {
    name: school?.shortName ?? school?.name ?? 'SchoolEduca',
    subtitle,
    logoUrl: school?.logoUrl,
    homeHref,
  }

  return (
    <CallProvider>
      <div className="min-h-dvh bg-canvas">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:rounded-control focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:shadow-overlay"
        >
          {t('Skip to content')}
        </a>

        <aside className="fixed inset-y-0 left-0 z-40 hidden w-sidebar border-r border-line lg:block print:hidden">
          <Sidebar groups={groups} brand={brand} />
        </aside>

        <Drawer
          open={navOpen}
          onOpenChange={setNavOpen}
          side="left"
          title={t('Navigation')}
          bare
          className="max-w-[18rem]"
        >
          <Sidebar groups={groups} brand={brand} onNavigate={() => setNavOpen(false)} />
        </Drawer>

        <div className="lg:pl-sidebar print:pl-0">
          <Topbar
            onOpenNav={() => setNavOpen(true)}
            onOpenSearch={searchable ? () => setSearchOpen(true) : undefined}
            accountHref={accountHref}
            portalLabel={subtitle}
          />
          <main id="main-content" tabIndex={-1} className="bg-canvas-glow focus:outline-none print:bg-none">
            <div className="mx-auto w-full max-w-content px-4 py-6 sm:px-6 lg:px-8 lg:py-8 print:max-w-none print:p-0">
              <Outlet />
            </div>
          </main>
        </div>

        {/* Mounted only while open so every opening starts from a clean query. */}
        {searchable && searchOpen ? (
          <CommandPalette open onOpenChange={setSearchOpen} groups={groups} />
        ) : null}
      </div>
    </CallProvider>
  )
}
